import React, {FC} from 'react';
import {useAppSelector} from 'app/store';
import {Button, CaretLeftIcon} from 'components';
import {FormTextInput} from 'components/Basic/Form/V2';
import {selectUserProfile} from 'features/User';
import {useRequesting} from 'hooks';
import {ProviderRole, SliceStatus, TherapistProfile} from 'interfaces';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {setDigitalPracticeSessionRate} from '../../paymentActions';

import {SessionRateBanner} from './SessionRateBanner';

export const SessionRateSettings: FC<{
  onCancel: () => void;
}> = ({onCancel}) => {
  const {t} = useTranslation();
  const {role, sessionRate} = useAppSelector(
    selectUserProfile,
  ) as TherapistProfile;
  const dispatch = useDispatch();
  const sessionRateStatus = useRequesting('user');

  const {control, handleSubmit} = useForm({
    defaultValues: {
      minimumRate: sessionRate?.digitalPractice?.min || 0,
      maximumRate: sessionRate?.digitalPractice?.max || 0,
    },
  });

  const onSaveRate: SubmitHandler<{
    minimumRate: number;
    maximumRate: number;
  }> = data => {
    const {minimumRate: min, maximumRate: max} = data;
    dispatch(
      setDigitalPracticeSessionRate({role: role as ProviderRole, min, max}),
    );
  };

  const isSaving = sessionRateStatus === SliceStatus.pending;

  return (
    <div className="flex flex-col">
      <header className="flex items-center h-20 lg:h-12 px-5 lg:px-7 lg:pb-4 border-b">
        <button
          onClick={onCancel}
          className="inline-flex sm:hidden items-center justify-left cursor-pointer w-8 h-12 rounded-full"
        >
          <CaretLeftIcon fillColor="#000" width={18} height={18} />
        </button>
        <h2 className="font-inter text-lg leading-5 font-medium">
          {t('providerProfileMenuItems.revenue.dp.sessionRate.title')}
        </h2>
      </header>
      <div className="flex flex-col gap-6 lg:gap-5 px-5 pt-7 lg:pt-4 pb-5">
        <div className="order-2 md:order-none grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-5">
          <div className="w-full flex flex-col gap-6">
            <div className="group relative flex gap-2.5">
              <span className="absolute left-6 bottom-[17px] font-inter text-base leading-5 font-medium">
                $
              </span>
              <FormTextInput
                id="minimumRate"
                name="minimumRate"
                control={control}
                label={t(
                  'providerProfileMenuItems.revenue.dp.sessionRate.minAmt',
                )}
                labelClasses="font-inter text-sm leading-4 font-medium"
                inputClasses="font-inter text-base leading-5 font-medium py-4 px-10 rounded-xl"
                classes="!pt-0"
              />
              <span className="font-inter text-base leading-5 font-medium self-end relative bottom-4">
                /{t('providerProfileMenuItems.revenue.dp.sessionRate.hour')}
              </span>
            </div>

            <div className="group relative flex gap-2.5">
              <span className="absolute left-6 bottom-[17px] font-inter text-base leading-5 font-medium">
                $
              </span>
              <FormTextInput
                id="maximumRate"
                name="maximumRate"
                control={control}
                label={t(
                  'providerProfileMenuItems.revenue.dp.sessionRate.maxAmt',
                )}
                labelClasses="font-inter text-sm leading-4 font-medium"
                inputClasses="font-inter text-base leading-5 font-medium py-4 px-10 rounded-xl"
                classes="!pt-0"
              />
              <span className="font-inter text-base leading-5 font-medium self-end relative bottom-4">
                /{t('providerProfileMenuItems.revenue.dp.sessionRate.hour')}
              </span>
            </div>
          </div>
          <div className="w-full">
            <SessionRateBanner />
          </div>
        </div>
        <div className="order-1 md:order-none w-full rounded-xl bg-[#FFCB5826] px-6 py-4">
          <p className="font-inter text-sm font-medium text-[#5B4D2D]">
            {t('providerProfileMenuItems.revenue.dp.sessionRate.desc')}
          </p>
        </div>
      </div>
      <div className="w-full flex justify-end items-center gap-2.5 border-t px-5 pt-5">
        <Button
          type="button"
          btnType="primary"
          className="w-auto min-w-max h-12 flex justify-center items-center shadow-sm px-6 py-0 m-0 font-inter leading-5"
          fontSize="base"
          fontWeight="medium"
          borderRadius="xl"
          onClick={onCancel}
          outline
        >
          {t('cancel')}
        </Button>
        <Button
          type="button"
          btnType="primary"
          className="w-auto min-w-[96px] h-12 flex justify-center items-center shadow-sm px-6 py-0 m-0 font-inter leading-5"
          fontSize="base"
          fontWeight="medium"
          borderRadius="xl"
          loaderType="Oval"
          disabled={isSaving}
          isSubmitting={isSaving}
          onClick={handleSubmit(onSaveRate)}
        >
          {t('save')}
        </Button>
      </div>
    </div>
  );
};
